import { useContext, useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import Loading from "../../Loading";
import { RebalanceContext } from "../RebalanceInfo";
import AccountBlock from "./account/AccountBlock";
import ExplainHeader from "./ExplainHeader";
import { useExplain } from "./hooks/useExplain";
import {
  DisplayModeContext,
  type DisplayMode,
  loadInitialDisplayMode,
  persistDisplayMode,
} from "./hooks/useDisplayMode";
import AuditFindingCards from "./panels/AuditFindingCards";
import ChecksPanel from "./panels/ChecksPanel";
import FinalPanel from "./panels/FinalPanel";
import ModelAssignmentPanel from "./panels/ModelAssignmentPanel";
import ProposalRequestPanel from "./panels/ProposalRequestPanel";
import TriggerPanel from "./panels/TriggerPanel";
import SummaryTable from "./summary/SummaryTable";
import type { ExplainResponseV3 } from "./lib/explainTypes";

interface Props {
  // When supplied, render this v3 explain payload directly and skip the
  // id-based `useExplain` fetch. Used by the synchronous, unpersisted
  // manual rebalance, which receives its `ExplainResponseV3` inline from
  // the api (pivoted from python's `explain_v3_rows`). See APP-1248.
  response?: ExplainResponseV3;
}

// Architecture spec §8: slim page (~100 lines), data fetch + tree
// composition only. Each child reads only the slice it renders --
// no cross-step joins, no client-side reshaping.
//
// Mounted at route `/rebalances/:rebalanceId/explain` — the default
// explain view. rebalanceId is read from RebalanceContext, populated by
// the parent `<RebalanceInfo>` route component from the URL parameter.
export default function RebalanceHouseholdExplain({
  response: providedResponse,
}: Props = {}) {
  const { rebalance } = useContext(RebalanceContext);
  const rebalanceId = rebalance?.id;
  // When a response prop is provided (manual rebalance), skip the fetch:
  // pass `undefined` so `useExplain` stays disabled (`enabled: rebalanceId
  // != null`) and the hook is still called unconditionally per the rules
  // of hooks.
  const { data, isLoading, error } = useExplain(
    providedResponse ? undefined : rebalanceId,
  );

  const [mode, setMode] = useState<DisplayMode>(loadInitialDisplayMode);
  useEffect(() => persistDisplayMode(mode), [mode]);

  if (!providedResponse) {
    if (!rebalanceId || isLoading) return <Loading />;
    if (error) {
      return (
        <Alert variant="danger">Failed to load explain: {String(error)}</Alert>
      );
    }
  }

  const response = providedResponse ?? data?.data ?? null;

  if (response === null) {
    // Pre-v3 rebalance has no v3:* rows. Per architecture spec §7.4
    // (legacy-divergence): such rebalances render through the legacy
    // explain UI, now mounted at `explain/old`.
    return (
      <Alert variant="info">
        This rebalance predates the v3 explain pipeline.{" "}
        <Link to="../explain/old">View it in the legacy explain view.</Link>
      </Alert>
    );
  }

  return (
    <DisplayModeContext.Provider value={{ mode, setMode }}>
      <ExplainHeader
        rebalanceId={response.rebalance_id}
        generatedAt={response.generated_at}
        householdId={response.request.household_id}
      />
      <ChecksPanel
        rebalanceId={response.rebalance_id}
        accounts={response.accounts}
      />
      <TriggerPanel trigger={response.trigger} />
      <ProposalRequestPanel
        request={response.request}
        accounts={response.accounts}
      />
      {/* D8: audit findings render inline on the panel named by each finding's
          `checkpoint` (R10). Sourced from the third primed cache stream
          (`useAudit`) — no separate view, no fork. `holdings` is the proposal
          request / starting-state checkpoint. */}
      <AuditFindingCards checkpoint="holdings" />
      <ModelAssignmentPanel
        modelAssignment={response.model_assignment}
        accounts={response.accounts}
        householdModel={response.request.household_model}
      />
      <AuditFindingCards checkpoint="model" />
      <AuditFindingCards checkpoint="cash" />
      <AuditFindingCards checkpoint="restrictions" />
      <AuditFindingCards checkpoint="substitutions" />
      <AuditFindingCards checkpoint="constrain" />
      {/* The soft-completion view's own sections supersede the generic
          summary pivot; it stays for every other strategy. */}
      {response.model_assignment?.strategy !== "soft_completion" && (
        <SummaryTable accounts={response.accounts} />
      )}
      {response.accounts.map((a) => (
        <AccountBlock key={a.account_id} account={a} />
      ))}
      <FinalPanel final={response.final} />
      <AuditFindingCards checkpoint="final" />
    </DisplayModeContext.Provider>
  );
}
